import { memo, useCallback } from 'react';
import { SortDirection } from '@components/Table/model/table';
import TableHeaderCell from '../../TableHeaderCell/ui/TableHeaderCell';
import TableHeaderSelect from '../../TableHeaderSelect/ui/TableHeaderSelect';
import styles from './TableHeader.module.scss';

interface ITableHeaderProps {
  activeSortColumnKey: string;
  sortDirection: SortDirection;
  onSortClick: (columnName: string) => void;
  columns: any[];
  selectable?: boolean;
  handleSelectRows: () => void;
  selectedRows?: string[];
  data: any[];
}

/**
 * @param props - The props for the component.
 * @param columns - The columns array.
 * @param onSortClick - Function which is called after clicking on sort
 * @param activeSortColumnKey - Active colum key
 * @param sortDirection - Sort direction of the table ascending | descending
 * @returns A React component that displays the table header.
 */

function TableHeaderComponent(props: ITableHeaderProps) {
  const {
    activeSortColumnKey,
    sortDirection,
    onSortClick,
    selectedRows,
    selectable,
    columns,
    data,
    handleSelectRows,
  } = props;

  const onSortClickHandler = useCallback(
    (key: string) => {
      onSortClick(key);
    },
    [onSortClick]
  );

  const renderHeaderColumns = useCallback(() => {
    return (
      <>
        {selectable && (
          <TableHeaderSelect
            column
            onSelect={handleSelectRows}
            selected={selectedRows?.length === data.length}
          />
        )}
        {columns.map(({ name, width, key, sortable }: any) => {
          const columnKey = key ?? name;
          return (
            <TableHeaderCell
              key={`${columnKey}`}
              onSortClick={onSortClickHandler}
              name={name}
              width={width}
              colKey={key}
              active={activeSortColumnKey === key}
              sortDirection={sortDirection}
              sortable={sortable}
            />
          );
        })}
      </>
    );
  }, [
    columns,
    sortDirection,
    activeSortColumnKey,
    selectable,
    handleSelectRows,
    selectedRows,
    data,
    onSortClickHandler,
  ]);

  return <div className={styles.tableHeader}>{renderHeaderColumns()}</div>;
}

const TableHeader = memo(TableHeaderComponent);

export default TableHeader;
